import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { WEB_PROJECT_TYPES, WEB_CMS } from "@/lib/constants";

const sel = "w-full border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-300";

const EMPTY = {
  client_id: "", new_client: false, new_name: "", new_email: "",
  name: "", type: "vitrine", cms: "wordpress", current_url: "", domain: "",
  description: "", objective: "", webmaster: "", priority: "normale",
  amount: "", deposit: "", pages_count: "", start_date: "", delivery_date: "",
  golive_date: "", estimated_hours: "", maintenance_included: false,
};

export default function WebProjectModal({ open, onClose, onCreated, clients, presetClientId = null }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && presetClientId) setF((p) => ({ ...p, client_id: presetClientId, new_client: false }));
  }, [open, presetClientId]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async () => {
    if (!presetClientId && !f.client_id && !(f.new_client && f.new_name.trim())) {
      toast.error("Sélectionnez un client ou créez-en un"); return;
    }
    setBusy(true);
    try {
      let clientId = presetClientId || f.client_id;
      if (f.new_client) {
        const c = await api.post("/clients", {
          name: f.new_name.trim(), email: f.new_email.trim(), services: ["site_web"],
        });
        clientId = c.data.id;
      }
      const { data } = await api.post("/web/projects", {
        client_id: clientId, name: f.name, type: f.type, cms: f.cms,
        current_url: f.current_url, domain: f.domain, description: f.description,
        objective: f.objective, webmaster: f.webmaster, priority: f.priority,
        amount: Number(f.amount) || 0, deposit: Number(f.deposit) || 0,
        pages_count: Number(f.pages_count) || 0,
        start_date: f.start_date || null, delivery_date: f.delivery_date || null,
        golive_date: f.golive_date || null,
        estimated_minutes: (Number(f.estimated_hours) || 0) * 60,
        maintenance_included: f.maintenance_included,
      });
      toast.success("Projet web créé");
      setF(EMPTY);
      onCreated?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la création"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="web-project-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Nouveau projet web</DialogTitle>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            {!presetClientId && (
              <div className="col-span-2">
                <Label className="text-xs">Client associé *</Label>
                <select className={`${sel} mt-1`} value={f.new_client ? "__new__" : f.client_id}
                  onChange={(e) => e.target.value === "__new__"
                    ? setF({ ...f, new_client: true, client_id: "" })
                    : setF({ ...f, new_client: false, client_id: e.target.value })}
                  data-testid="web-client">
                  <option value="">— Sélectionner un client —</option>
                  {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  <option value="__new__">+ Nouveau client</option>
                </select>
              </div>
            )}
            {f.new_client && (
              <>
                <div><Label className="text-xs">Nom du nouveau client *</Label>
                  <Input className="mt-1" value={f.new_name} onChange={set("new_name")} data-testid="web-new-client-name" /></div>
                <div><Label className="text-xs">Email</Label>
                  <Input className="mt-1" value={f.new_email} onChange={set("new_email")} data-testid="web-new-client-email" /></div>
              </>
            )}
            <div className="col-span-2"><Label className="text-xs">Nom du projet *</Label>
              <Input className="mt-1" value={f.name} onChange={set("name")} placeholder="Ex : Site vitrine BTP Savoie" data-testid="web-name" /></div>
            <div><Label className="text-xs">Type de projet</Label>
              <select className={`${sel} mt-1`} value={f.type} onChange={set("type")} data-testid="web-type">
                {WEB_PROJECT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select></div>
            <div><Label className="text-xs">CMS</Label>
              <select className={`${sel} mt-1`} value={f.cms} onChange={set("cms")} data-testid="web-cms">
                {WEB_CMS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select></div>
            <div><Label className="text-xs">URL du site actuel (si applicable)</Label>
              <Input className="mt-1" value={f.current_url} onChange={set("current_url")} placeholder="https://…" data-testid="web-current-url" /></div>
            <div><Label className="text-xs">Domaine prévu</Label>
              <Input className="mt-1" value={f.domain} onChange={set("domain")} placeholder="exemple.fr" data-testid="web-domain" /></div>
            <div className="col-span-2"><Label className="text-xs">Description du projet</Label>
              <Textarea className="mt-1" rows={2} value={f.description} onChange={set("description")} data-testid="web-description" /></div>
            <div className="col-span-2"><Label className="text-xs">Objectif principal</Label>
              <Input className="mt-1" value={f.objective} onChange={set("objective")} placeholder="Ex : générer des demandes de devis" data-testid="web-objective" /></div>
            <div><Label className="text-xs">Webmaster responsable</Label>
              <Input className="mt-1" value={f.webmaster} onChange={set("webmaster")} data-testid="web-webmaster" /></div>
            <div><Label className="text-xs">Priorité</Label>
              <select className={`${sel} mt-1`} value={f.priority} onChange={set("priority")} data-testid="web-priority">
                {["basse", "normale", "haute", "urgente"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select></div>
          </div>

          <h3 className="font-heading font-bold text-sm pt-2 border-t border-neutral-100">Informations commerciales</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Montant du projet (€ TTC)</Label>
              <Input type="number" step="0.01" className="mt-1" value={f.amount} onChange={set("amount")} data-testid="web-amount" /></div>
            <div><Label className="text-xs">Acompte prévu (€)</Label>
              <Input type="number" step="0.01" className="mt-1" value={f.deposit} onChange={set("deposit")} data-testid="web-deposit" /></div>
            <div><Label className="text-xs">Nombre de pages incluses</Label>
              <Input type="number" className="mt-1" value={f.pages_count} onChange={set("pages_count")} data-testid="web-pages-count" /></div>
            <label className="flex items-center gap-2 text-sm mt-5 cursor-pointer" data-testid="web-maintenance">
              <input type="checkbox" className="rounded" checked={f.maintenance_included} onChange={set("maintenance_included")} />
              Maintenance incluse
            </label>
          </div>

          <h3 className="font-heading font-bold text-sm pt-2 border-t border-neutral-100">Planning</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date de démarrage</Label>
              <Input type="date" className="mt-1" value={f.start_date} onChange={set("start_date")} data-testid="web-start-date" /></div>
            <div><Label className="text-xs">Livraison prévisionnelle</Label>
              <Input type="date" className="mt-1" value={f.delivery_date} onChange={set("delivery_date")} data-testid="web-delivery-date" /></div>
            <div><Label className="text-xs">Mise en ligne souhaitée</Label>
              <Input type="date" className="mt-1" value={f.golive_date} onChange={set("golive_date")} data-testid="web-golive-date" /></div>
            <div><Label className="text-xs">Temps estimé (heures)</Label>
              <Input type="number" step="0.5" className="mt-1" value={f.estimated_hours} onChange={set("estimated_hours")} data-testid="web-estimated" /></div>
          </div>

          <Button onClick={submit} disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="web-submit">
            {busy ? "Création…" : "Créer le projet web"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
